import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "../../styles/LevelConfigurations.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import EllipseImg from "../assets/Ellipse.png";
import openNotification from "components/utils/CustomNotification";
import ConfirmationDialog from "components/utils/ConfirmationDialog";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";

interface OrgInfo {
  org_uid: string;
  active_user_count: number;
  inactive_user_count: number;
  admins: string[];
}

interface Level {
  id: number;
  title: string;
  isDefault: boolean;
  description: string;
  minPoints: number;
  maxPoints: number;
  image?: string;
  image_uid?: string;
}

const API_URL_ORGINFO = `${process.env.REACT_APP_API_URL}/api/org-info`;

const LevelConfigurations: React.FC = () => {
  useCheckSessionHeartBeat();
  const location = useLocation();
  const navigate = useNavigate();
  const orgDetailsFromNav = location.state?.orgDetails;
  const [levels, setLevels] = useState<Level[]>([]);
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState<OrgInfo[]>([]);
  const [selectedOrgUid, setSelectedOrgUid] = useState<string>("");
  const [editingLevel, setEditingLevel] = useState<Level | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [levelToDelete, setLevelToDelete] = useState<Level | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [character, setCharacter] = useState("");
  const [description, setDescription] = useState("");
  const [minPoints, setMinPoints] = useState<number | null>(null);
  const [maxPoints, setMaxPoints] = useState<number | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";

  const privilege = localStorage.getItem("privilege");

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await axios.get(API_URL_ORGINFO, {
          headers: {
            Accept: "application/json",
            "user-uid": userUid,
          },
          withCredentials: true,
        });
        if (response.status === 200) {
          const orgs = response.data.data || [];
          setOrganizations(orgs);
          if (orgs.length > 0) {
            setSelectedOrgUid(orgs[0].org_uid); // default to first org
          }
        } else {
          throw new Error("Failed to fetch organizations");
        }
      } catch (error) {
        console.error("Error fetching organizations:", error);
        openNotification({
          type: "error",
          message: "Failed to fetch organizations",
        });
      }
    };

    fetchOrganizations();
  }, [sessionUid, userUid]);

  const resetForm = () => {
    setEditingLevel(null);
    setCharacter("");
    setDescription("");
    setMinPoints(null);
    setMaxPoints(null);
    setImageFile(null);
    setImagePreview(null);
    setShowModal(false);
  };

  const fetchImagesByUid = async (
    imageUids: string[],
  ): Promise<Record<string, string>> => {
    if (!imageUids || imageUids.length === 0) {
      return {};
    }

    try {
      const url = new URL(`${beAddress}/api/get-image-by-uid`);
      imageUids.forEach((uid) =>
        url.searchParams.append("image_uid_list", uid),
      );
      url.searchParams.set("is_icon_img", "false");
      url.searchParams.set("is_feedback_image", "false");

      const res = await fetch(url.toString(), {
        headers: {
          "user-uid": userUid,
        },
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "Success" && Array.isArray(data.data)) {
        return Object.fromEntries(
          data.data.map((item: any) => {
            if (!item.image) return [item.image_uid, EllipseImg];

            // Prepend Base64 prefix
            const imageSrc = `data:image/png;base64,${item.image}`;
            return [item.image_uid, imageSrc];
          }),
        );
      }
    } catch (err) {
      console.error("Failed to fetch images:", err);
    }

    return {};
  };

  const fetchLevels = async () => {
    let orgUid = orgDetailsFromNav?.org_uid;
    if (privilege === "orgadmin" && !orgUid) {
      //if you store org_uid in localStorage or user profile
      orgUid = localStorage.getItem("org_uid");
    }

    try {
      const response = await axios.get(`${beAddress}/api/level-config`, {
        headers: {
          "user-uid": userUid,
        },
        withCredentials: true,
        params: {
          org_uid: orgUid,
        },
      });

      if (response.data?.status === "Success") {
        const orgData =
          response.data.data.find(
            (org: any) => org.org_uid === orgUid || org.org_uid === "system",
          ) || response.data.data[0];

        if (!orgData || !orgData.level_config) {
          setLevels([]);
          openNotification({
            type: "error",
            message: "No level configuration found for this organization",
          });
          return;
        }

        const imageUids = orgData.level_config
          .map((lvl: any) => lvl.image_uid)
          .filter((uid: string | undefined) => !!uid);

        // Fetch actual image URLs
        const imageMap = await fetchImagesByUid(imageUids);

        const data: Level[] = orgData.level_config.map((lvl: any) => ({
          id: lvl.level,
          title: lvl.character,
          isDefault: lvl.level === 0,
          description: lvl.description,
          minPoints: lvl.min_experience_points,
          maxPoints: lvl.max_experience_points,
          image: lvl.image_uid
            ? imageMap[lvl.image_uid] || EllipseImg
            : EllipseImg,
          image_uid: lvl.image_uid || undefined,
        }));

        setLevels(data);

        // openNotification({
        //   type: "success",
        //   message: response.data.message,
        // });
      } else {
        openNotification({
          type: "error",
          message: response.data?.data,
        });
      }
    } catch (error: any) {
      console.error("Error fetching levels:", error);
      openNotification({
        type: "error",
        message:
          error.response?.data?.data ||
          "Something went wrong while fetching levels",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedOrgUid) {
      fetchLevels();
    }
  }, [selectedOrgUid]);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };
  const handleSaveOrUpdate = async () => {
    try {
      let imageBase64: string | undefined;
      if (imageFile) {
        imageBase64 = await fileToBase64(imageFile);
      }

      const isDefault = editingLevel?.isDefault ?? false;

      // Conditional payload
      const payload = editingLevel
        ? {
            // PUT - flat payload
            level: editingLevel.id,
            character,
            description,
            min_experience_points: minPoints ?? 0,
            max_experience_points: maxPoints ?? 0,
            is_default: isDefault,
            ...(imageFile && imageBase64
              ? { level_img: imageBase64.split(",")[1] }
              : {}),
          }
        : {
            // POST - wrap inside level_config
            level_config: {
              level: 0,
              character:
                character || (isDefault ? "Default Level Configurations" : ""),
              description:
                description ||
                (isDefault
                  ? "Starting to walk like a pro, burning calories, gaining momentum."
                  : ""),
              min_experience_points: minPoints ?? 0,
              max_experience_points: maxPoints ?? 0,
              ...(imageFile && imageBase64
                ? { level_img: imageBase64.split(",")[1] }
                : {}),
            },
            is_default: isDefault,
          };

      const url = isDefault
        ? `${beAddress}/api/default-level-config`
        : `${beAddress}/api/level-config`;

      const method = editingLevel ? "put" : "post";

      const response = await axios({
        method,
        url,
        data: payload,
        headers: {
          // "session-uid": sessionUid,
          "user-uid": userUid,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (response.data?.status === "Success") {
        openNotification({
          type: "success",
          message: editingLevel
            ? isDefault
              ? "Default level updated successfully"
              : "Level updated successfully"
            : "Level added successfully",
        });
        resetForm();
        fetchLevels();
      } else {
        openNotification({
          type: "error",
          message: response.data?.message || "Failed to save level",
        });
      }
    } catch (error: any) {
      console.error("Error saving level:", error);
      openNotification({
        type: "error",
        message:
          error.response?.data?.message ||
          "Something went wrong while saving level",
      });
    }
  };

  // const handleSaveOrUpdate = async () => {
  //   try {
  //     let imageBase64: string | undefined;
  //     if (imageFile) {
  //       imageBase64 = await fileToBase64(imageFile);
  //     }

  //     const isDefault = editingLevel?.isDefault ?? false;

  //     const payload = {
  //       level_config: {
  //         level: editingLevel ? editingLevel.id : undefined,
  //         character,
  //         description,
  //         min_experience_points: isDefault ? 0 : minPoints ?? 0,
  //         max_experience_points: isDefault ? 0 : maxPoints ?? 0,
  //         ...(imageFile && imageBase64
  //           ? { level_img: imageBase64.split(",")[1] } // REMOVE data:image prefix
  //           : {}),
  //       },
  //       is_default: isDefault,
  //     };

  //     // const payload = {
  //     //   // org_uid: selectedOrgUid,
  //     //   org_level_config: {
  //     //     level: editingLevel ? editingLevel.id : undefined,
  //     //     character,
  //     //     description,
  //     //     ...(isDefault
  //     //       ? {
  //     //           min_experience_points: 0,
  //     //           max_experience_points: "0",
  //     //         }
  //     //       : {
  //     //           min_experience_points: minPoints ?? 0,
  //     //           max_experience_points: maxPoints ?? 0,
  //     //         }),
  //     //     ...(imageFile ? { level_img: imageBase64 } : {}),
  //     //   },
  //     // };

  //     const url = isDefault
  //       ? `${beAddress}/api/default-level-config`
  //       : `${beAddress}/api/level-config`;

  //     const method = editingLevel ? "put" : "post";

  //     const response = await axios({
  //       method,
  //       url,
  //       data: payload,
  //       headers: {
  //         "session-uid": sessionUid,
  //         "user-uid": userUid,
  //         "Content-Type": "application/json",
  //       },
  //     });

  //     if (response.data?.status === "Success") {
  //       openNotification({
  //         type: "success",
  //         message: editingLevel
  //           ? isDefault
  //             ? "Default level updated successfully"
  //             : "Level updated successfully"
  //           : "Level added successfully",
  //       });
  //       setShowModal(false);
  //       setEditingLevel(null);
  //       fetchLevels();
  //     } else {
  //       openNotification({
  //         type: "error",
  //         message: response.data?.message || "Failed to save level",
  //       });
  //     }
  //   } catch (error: any) {
  //     console.error("Error saving level:", error);
  //     openNotification({
  //       type: "error",
  //       message:
  //         error.response?.data?.message ||
  //         "Something went wrong while saving level",
  //     });
  //   }
  // };

  if (loading) {
    return <div className="levels-container">Loading levels...</div>;
  }
  const handleDelete = async (level: Level) => {
    try {
      const payload = {
        level: level.id,
        character: level.title,
        description: level.description,
        min_experience_points: level.minPoints ?? 0,
        max_experience_points: level.maxPoints ?? 0,
        ...(level.image_uid ? { image_uid: level.image_uid } : {}),
      };

      const response = await axios.delete(`${beAddress}/api/level-config`, {
        headers: {
          "user-uid": userUid,
          "Content-Type": "application/json",
        },
        withCredentials: true,
        data: payload, // axios DELETE must send body here
      });

      if (response.data?.status === "Success") {
        openNotification({
          type: "success",
          message: "Level deleted successfully",
        });
        fetchLevels();
      } else {
        openNotification({
          type: "error",
          message: response.data?.message || "Failed to delete level",
        });
      }
    } catch (error: any) {
      console.error("Error deleting level:", error);
      openNotification({
        type: "error",
        message:
          error.response?.data?.message ||
          "Something went wrong while deleting level",
      });
    }
  };

  // const handleDelete = async (level: Level) => {
  //   try {
  //     const payload = {
  //       // org_uid: selectedOrgUid,
  //       org_level_config: {
  //         character: level.title,
  //         description: level.description,
  //         min_experience_points: level.minPoints,
  //         max_experience_points: level.maxPoints,
  //         ...(level.image_uid ? { image_uid: level.image_uid } : {}),
  //       },
  //     };

  //     const response = await axios.delete(`${beAddress}/api/level-config`, {
  //       headers: {
  //         "session-uid": sessionUid,
  //         "user-uid": userUid,
  //         "Content-Type": "application/json",
  //       },
  //       data: payload, // axios DELETE must send body here
  //     });

  //     if (response.data?.status === "Success") {
  //       openNotification({
  //         type: "success",
  //         message: "Level deleted successfully",
  //       });
  //       fetchLevels();
  //     } else {
  //       openNotification({
  //         type: "error",
  //         message: response.data?.message || "Failed to delete level",
  //       });
  //     }
  //   } catch (error: any) {
  //     console.error("Error deleting level:", error);
  //     openNotification({
  //       type: "error",
  //       message:
  //         error.response?.data?.message ||
  //         "Something went wrong while deleting level",
  //     });
  //   }
  // };

  return (
    <div className="levels-container">
      <div className="levels-header">Levels Configuration</div>

      {levels.map((level) => (
        <div key={level.id} className="level-card">
          <div className="level-number">{level.id}</div>
          <div className="level-content">
            <h3 className="level-title">
              {level.title}{" "}
              {level.isDefault && (
                <span className="default-tag">(default)</span>
              )}
            </h3>
            <p className="level-description">{level.description}</p>
            <div className="points-row">
              <span>
                <strong>Min experience points:</strong> {level.minPoints}
              </span>
              <span>
                <strong>Max experience points:</strong> {level.maxPoints}
              </span>
            </div>
          </div>
          <div className="level-right">
            <img src={level.image} alt={level.title} className="level-image" />
            <div className="icon-row">
              {privilege !== "systemadmin" && (
                <>
                  {/* Edit icon - still visible for default level */}
                  <FiEdit2
                    className="icon edit"
                    onClick={() => {
                      setEditingLevel(level);
                      setCharacter(level.title);
                      setDescription(level.description);
                      setMinPoints(level.minPoints ?? null);
                      setMaxPoints(level.maxPoints ?? null);
                      setImagePreview(level.image || null);
                      setShowModal(true);
                    }}
                  />

                  {/* Delete icon - hidden for default level */}
                  {!level.isDefault && (
                    <FiTrash2
                      className="icon delete"
                      onClick={() => {
                        setLevelToDelete(level);
                        setShowConfirmModal(true);
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ))}
      {privilege !== "systemadmin" && (
        <>
          <button
            className="add-level-btn"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            Add New Level +
          </button>
        </>
      )}
      {showModal && (
        <div className="addlevelmodal-overlay">
          <div className="addlevelmodal">
            <div className="addlevelmodal-header">
              <h3>{editingLevel ? "Edit Level" : "Add New Level"}</h3>
              <button
                className="addlevelmodal-close-btn"
                onClick={() => resetForm()}
              >
                ×
              </button>
            </div>

            <div className="addlevelmodal-form-group">
              <label>Character</label>
              <input
                type="text"
                maxLength={30}
                value={character}
                onChange={(e) => setCharacter(e.target.value)}
              />
            </div>

            <div className="addlevelmodal-form-group">
              <label>Description</label>
              <textarea
                maxLength={70}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="addlevelmodal-form-row">
              <div className="addlevelmodal-form-group">
                <label>Min Points</label>
                <input
                  type="number"
                  min="0"
                  max="99999999"
                  value={minPoints === null ? "" : minPoints}
                  disabled={editingLevel?.isDefault}
                  onChange={(e) => {
                    if (e.target.value.length <= 8) {
                      setMinPoints(
                        e.target.value === "" ? null : Number(e.target.value),
                      );
                    }
                  }}
                />
              </div>

              <div className="addlevelmodal-form-group">
                <label>Max Points</label>
                <input
                  type="number"
                  min="0"
                  max="99999999"
                  value={maxPoints === null ? "" : maxPoints}
                  disabled={editingLevel?.isDefault}
                  onChange={(e) => {
                    if (e.target.value.length <= 8) {
                      setMaxPoints(
                        e.target.value === "" ? null : Number(e.target.value),
                      );
                    }
                  }}
                />
              </div>
              <div className="addlevelmodal-form-group">
                <label>Image</label>
                <input
                  type="file"
                  accept="image/*"
                  className={imagePreview ? "hide-no-file-text" : ""}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setImageFile(file);
                      setImagePreview(URL.createObjectURL(file));
                    }
                  }}
                />
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Level Preview"
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "cover",
                      marginTop: "8px",
                    }}
                  />
                )}
              </div>
            </div>

            <div className="addlevelmodal-actions">
              <button
                className="addlevelmodal-btn save"
                onClick={handleSaveOrUpdate}
              >
                {editingLevel ? "Update" : "Save"}
              </button>
              {/* <button
    className="addlevelmodal-btn cancel"
    onClick={() => {
      setShowModal(false);
      setEditingLevel(null);
    }}
  >
    Cancel
  </button> */}
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && levelToDelete && (
        <ConfirmationDialog
          show={showConfirmModal}
          message={`Are you sure you want to delete level "${levelToDelete.title}"?`}
          onConfirm={() => {
            handleDelete(levelToDelete);
            setShowConfirmModal(false);
            setLevelToDelete(null);
          }}
          onCancel={() => {
            setShowConfirmModal(false);
            setLevelToDelete(null);
          }}
          onClose={() => {
            setShowConfirmModal(false);
            setLevelToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default LevelConfigurations;
